/*
 * Назначение: выбор моделей служебной задачи ИИ (заявка, выжимка, напоминание).
 * Функционал: галочки по моделям проекта — провайдер берётся тот же, что указан
 * у модели на вкладке «Модели», поэтому связка «модель + провайдер» задаётся в
 * одном месте и не расходится. Ничего не выбрано = задача работает на моделях
 * проекта, как основной чат. Модели, сохранённые ранее и отсутствующие в списке
 * проекта, показываются отдельно — чтобы настройка не пропадала молча.
 */

import type { AiProvider } from "../../../lib/admin-api";
import type { ModelConfigEntry, ModelRouteEntry } from "../../../lib/types";
import { Badge } from "../../components/ui/badge";
import { Checkbox } from "../../components/ui/checkbox";

import { providerLabel } from "./provider-select";

const sameRoute = (a: ModelRouteEntry, b: ModelRouteEntry) =>
  a.model === b.model && (a.provider_id || "") === (b.provider_id || "");

export function TaskModelsPicker({
  projectModels,
  providers,
  selected,
  onChange,
}: {
  projectModels: ModelConfigEntry[];
  providers: AiProvider[];
  selected: ModelRouteEntry[];
  onChange: (models: ModelRouteEntry[]) => void;
}) {
  const available = projectModels.filter((m) => m.active !== false);
  // Сохранённые ранее маршруты, которых больше нет в списке проекта.
  const orphans = selected.filter(
    (s) => !available.some((m) => sameRoute(m, s)),
  );

  const toggle = (route: ModelRouteEntry, checked: boolean) => {
    if (!checked) {
      onChange(selected.filter((s) => !sameRoute(s, route)));
      return;
    }
    // Порядок приоритета — как в списке проекта, плюс «осиротевшие» в конце.
    const next = [...selected, { model: route.model, provider_id: route.provider_id }];
    const ordered = available
      .filter((m) => next.some((s) => sameRoute(s, m)))
      .map((m) => ({ model: m.model, provider_id: m.provider_id }));
    onChange([...ordered, ...orphans]);
  };

  if (available.length === 0 && orphans.length === 0) {
    return (
      <p className="text-xs text-muted-foreground">
        Сначала добавь модели проекта выше — задача пока работает на них же.
      </p>
    );
  }

  return (
    <div className="space-y-1">
      <p className="text-xs text-muted-foreground">
        Модели задачи (ничего не отмечено — как у чата)
      </p>
      {available.map((m) => {
        const checked = selected.some((s) => sameRoute(s, m));
        return (
          <label
            key={`${m.model}@${m.provider_id}`}
            className="flex cursor-pointer items-center gap-2 text-xs"
          >
            <Checkbox
              checked={checked}
              onCheckedChange={(v) =>
                toggle({ model: m.model, provider_id: m.provider_id }, Boolean(v))
              }
            />
            <span className="font-mono">{m.model}</span>
            <span className="text-muted-foreground">
              · {providerLabel(providers, m.provider_id)}
            </span>
          </label>
        );
      })}
      {orphans.map((s) => (
        <label
          key={`orphan-${s.model}@${s.provider_id}`}
          className="flex cursor-pointer items-center gap-2 text-xs"
        >
          <Checkbox checked onCheckedChange={() => toggle(s, false)} />
          <span className="font-mono">{s.model}</span>
          <Badge variant="secondary">нет в списке проекта</Badge>
        </label>
      ))}
    </div>
  );
}
